/**
 * beneficialOwners — FinCEN CDD Beneficial Ownership model + requirement map.
 *
 * BACKLOG-0721b (Bill 2026-06-01): Beneficial Ownership is ENTITY-scoped and
 * lives on the business-entity person record (`person.extra.beneficialOwners`),
 * NOT on the product modal. POD beneficiaries (the other half) are product-
 * scoped and live in AddToCartModal — see feedback_beneficial_ownership_on_
 * entity_modal_beneficiaries_on_product_modal_20260601.
 *
 * Regulatory basis: FinCEN CDD Rule (31 CFR 1010.230) — for each "legal entity
 * customer" capture every individual owning >=25% equity (ownership prong) plus
 * one individual with substantial control (control prong).
 *
 * Requirement map is keyed to the REAL CustomerType values from
 * business-config.ts BUSINESS_SUB_TYPES (the prior scaffold keyed 'Corp' which
 * never matched the stored 'Corporation' — i.e. it was dead). Each row cites the
 * canonical source per CLAUDE.md rule #1 (no guessing legacy procedure).
 *
 * Core Director destinations (canonical SPEC.md Screen 38 / index.html:3338-3414):
 *   ownership prong  -> CIF Fields 363-366 (BenflOwnInfo, BenflOwnType Own)
 *   control prong    -> CIF Field 367      (BenflOwnInfo, BenflOwnType Cntl)
 *   owner+controller -> 363-366 + 367 on one CIF row (BenflOwnType CntlOwn)
 *
 * NOTE on the "no individual owns 25%+" toggle: this is the ownership-prong
 * outcome where no 25% owner exists (a control person is STILL required) — it
 * is NOT the same as a full CDD exemption. CIF Field 378 ("Exempt Person Class",
 * e.g. 15=Public Funds, SPEC.md:761) is an ENUMERATED code for CDD-exempt
 * entities (the 'none' subtypes), not a free-text basis. The basis captured by
 * the toggle is a UI/disclosure flag pending the Field-378 enum (tracked).
 *
 * AcctRelCode mapping: Own->BO, Cntl->CT, CntlOwn->CO (SPEC.md:920,3437).
 */

/** Per-owner beneficial-ownership entry — persisted in person.extra.beneficialOwners. */
export interface BeneficialOwnerEntry {
  /** Stable UI id. */
  id: string;
  /** Full legal name of the beneficial owner / controller. */
  name: string;
  /** Control-person title (CEO/CFO/...); free on 'Other'. */
  title?: string;
  /** Equity ownership %, ownership prong. CIF 363-366. Omit/0 for control-only. */
  ownershipPct?: number;
  /** True = substantial-control person (control prong, CIF 367). */
  isController: boolean;
  /** YYYY-MM-DD. */
  dob?: string;
  /** Raw 9-digit SSN/EIN (unmasked) — BenflOwnInfo.TaxId on CustRelAdd. */
  ssn?: string;
  addr?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  /** DL / StateID / Passport / Other. */
  idType?: string;
  idNumber?: string;
  idIssuer?: string;
}

/** What the FinCEN CDD worksheet must collect for a given entity subtype. */
export type BoRequirementKind =
  | 'full'            // >=25% owners (ownership prong) + 1 controller (control prong)
  | 'controllerOnly'  // controller only — no ownership prong
  | 'none';           // not a legal-entity customer / CDD-exempt

export interface BoRequirement {
  kind: BoRequirementKind;
  /** One-line citation of why this subtype maps here. */
  citation: string;
}

/**
 * Map CustomerType -> BO requirement. Sources cited per CLAUDE.md rule #1.
 * Canonical authority: SPEC.md:3059 + index.html:3344-3345 (Screen 38).
 */
const BO_REQUIREMENT: Readonly<Record<string, BoRequirement>> = {
  // FULL — ownership prong (>=25%) + control prong.
  Corporation:  { kind: 'full', citation: 'SPEC.md:3059 — Corp = FULL (BO + Controller)' },
  LLC:          { kind: 'full', citation: 'SPEC.md:3059 — LLC = FULL (BO + Controller)' },
  Partnership:  { kind: 'full', citation: 'SPEC.md:3059 — Partnership/LP = FULL (BO + Controller)' },
  NonProfit:    { kind: 'full', citation: 'SPEC.md:3059 — Non-Profit Corp = FULL (BO + Controller)' },
  IOLTA:        { kind: 'full', citation: '31 CFR 1010.230 legal-entity customer (MO lawyer trust opened by a law firm/entity) — confirm scope w/ Collette' },
  Political:    { kind: 'full', citation: 'SPEC.md:3059,762 — Political Committee = FULL (recursion supported)' },
  // ICS_Business is a business legal entity → FinCEN default is FULL. No explicit
  // exemption citation; conservative CDD posture (over-collect safer than under).
  ICS_Business: { kind: 'full', citation: 'FinCEN default for legal-entity customer (no exemption cited — confirm w/ Collette)' },

  // CONTROLLER ONLY — control prong, no ownership prong.
  UnincorpAssoc: { kind: 'controllerOnly', citation: 'SPEC.md:3059 — Unincorp Assoc = CONTROLLER_ONLY' },

  // NONE — sole proprietor is the customer (not a separate legal entity) → no BO.
  DBA: { kind: 'none', citation: 'Sole proprietor — not a legal-entity customer under 31 CFR 1010.230' },
};

/** Resolve the BO requirement for a customerType. Unknown/personal → none. */
export function boRequirementFor(customerType: string | undefined | null): BoRequirement {
  if (!customerType) return { kind: 'none', citation: 'No customerType' };
  return BO_REQUIREMENT[customerType] ?? {
    kind: 'none',
    citation: `No BO mapping for '${customerType}' — treated as non-legal-entity`,
  };
}

/** Control-person title options (SPFx BeneficialOwnerSection.tsx:23-26). */
export const BO_TITLE_OPTIONS: ReadonlyArray<string> = [
  'CEO', 'CFO', 'COO', 'President', 'Vice President', 'Treasurer', 'Secretary',
  'Managing Member', 'General Partner', 'Sole Proprietor', 'Director', 'Trustee', 'Other',
];

/** Government-ID type options for the beneficial owner. */
export const BO_ID_TYPE_OPTIONS: ReadonlyArray<string> = [
  'Driver License', 'State ID', 'Passport', 'Other',
];

/** Derived jXChange BenflOwnType for a row (SPEC.md:3055; JxChangeService BenflOwnType). */
export function benflOwnType(e: Pick<BeneficialOwnerEntry, 'ownershipPct' | 'isController'>): 'Own' | 'Cntl' | 'CntlOwn' {
  const owns = (e.ownershipPct ?? 0) >= 25;
  if (owns && e.isController) return 'CntlOwn';
  if (e.isController) return 'Cntl';
  return 'Own';
}

/** Derived AcctRelCode for a row (Own->BO, Cntl->CT, CntlOwn->CO; SPEC.md:920,3437). */
export function boAcctRelCode(e: Pick<BeneficialOwnerEntry, 'ownershipPct' | 'isController'>): 'BO' | 'CT' | 'CO' {
  switch (benflOwnType(e)) {
    case 'CntlOwn': return 'CO';
    case 'Cntl':    return 'CT';
    default:        return 'BO';
  }
}

/** Read the BO roster off a person record (person.extra.beneficialOwners). */
export function readBeneficialOwners(extra: Record<string, unknown> | undefined): BeneficialOwnerEntry[] {
  const arr = extra?.['beneficialOwners'];
  if (!Array.isArray(arr)) return [];
  return arr.filter((x): x is BeneficialOwnerEntry => !!x && typeof x === 'object');
}

/** Read the entity-level BO-exempt flag/reason (Field 378). */
export function readBoExempt(extra: Record<string, unknown> | undefined): { exempt: boolean; reason: string } {
  return {
    exempt: extra?.['boExempt'] === true,
    reason: typeof extra?.['boExemptReason'] === 'string' ? (extra['boExemptReason'] as string) : '',
  };
}

/**
 * Mirror entry shape written to cart.metadataJson.beneficialOwners so the
 * compliance gates (useWorkflowGates / complianceRulesEngine / BusinessGates)
 * keep their existing read contract. Carries BOTH `percent` (gate field,
 * types.ts:137) and `ownershipPct` (canonical) so no gate edit is needed.
 */
export interface BoMirrorEntry {
  id: string;
  name: string;
  title?: string;
  ownershipPct: number;
  percent: number;
  isController: boolean;
  benflOwnType: 'Own' | 'Cntl' | 'CntlOwn';
}

/** Map a person's BO roster to the gate mirror shape. */
export function toBoMirror(entries: ReadonlyArray<BeneficialOwnerEntry>): BoMirrorEntry[] {
  return entries.map((e) => {
    const pct = e.ownershipPct ?? 0;
    return {
      id: e.id,
      name: e.name,
      ...(e.title ? { title: e.title } : {}),
      ownershipPct: pct,
      percent: pct,
      isController: e.isController,
      benflOwnType: benflOwnType(e),
    };
  });
}
